import { inject } from "@angular/core";
import { CanDeactivateFn } from "@angular/router";
import { AlertController } from "@ionic/angular";
import { Observable, from, of } from "rxjs";
import { switchMap, take } from "rxjs/operators";
import { FormGroupState } from "ngrx-forms";
import { FormDraftSessionService } from "@app/services";

export interface isDeactivatable {
  form$: Observable<FormGroupState<unknown>>;
  canLeavePage: () => Observable<boolean> | Promise<boolean> | boolean;
  canKeepDraft?: boolean;
}

export const leavePageGuard: CanDeactivateFn<isDeactivatable> = (
  component
): Observable<boolean> => {
  const alertController = inject(AlertController);
  const drafts = inject(FormDraftSessionService);
  const canKeepDraft = component.canKeepDraft !== false;

  const alertControl = async (): Promise<boolean> => {
    const alert = await alertController.create({
      header: "Unsaved Changes",
      message: canKeepDraft
        ? "You can keep these changes as a draft or discard them."
        : "Discard the unsaved selection or stay on this page.",
      buttons: [
        {
          text: "Stay",
          role: "cancel",
        },
        {
          text: "Discard",
          role: "discard",
        },
        ...(canKeepDraft
          ? [
              {
                text: "Keep draft",
                role: "keepDraft",
              },
            ]
          : []),
      ],
    });

    await alert.present();
    const data = await alert.onDidDismiss();

    try {
      if (data.role === "discard") {
        if (canKeepDraft) await drafts.discardDraft();
        return true;
      }
      if (data.role === "keepDraft") {
        await drafts.keepDraft();
        return true;
      }
    } catch {
      const errorAlert = await alertController.create({
        header: "Draft Storage Error",
        message: "The draft could not be stored safely. Please try again.",
        buttons: ["OK"],
      });
      await errorAlert.present();
      return false;
    }
    return false;
  };

  return component.form$.pipe(
    take(1),
    switchMap((form) => {
      if (form.isPristine) {
        return of(true);
      } else {
        return from(alertControl());
      }
    })
  );
};
